import { onActivated, onMounted, onUnmounted, ref, type Ref } from 'vue'

import { eventEmitter, type TriggerListRefreshOptions } from '@/utils/events/event-emitter'

import { CommonEvent } from '@/constant/events'

interface UseListRefreshOptions<TData> {
  /**
   * 列表名稱，用於指定刷新目標
   * 當 triggerListRefresh 傳入相同名稱時才會刷新
   */
  listName?: string

  /**
   * 列表資料陣列（響應式）
   * 當傳入時，會使用前端刪除邏輯，避免重新 fetch API
   */
  list?: Ref<TData[]>

  /**
   * 用於匹配刪除項目的 ID 欄位名稱
   */
  idField?: keyof TData

  /**
   * 完整刷新的回調函式（當未傳入 removedId 時使用）
   */
  onRefresh?: () => void | Promise<void>

  /**
   * 是否在 onActivated 時立即檢查並執行操作
   * @default true
   */
  immediate?: boolean

  /**
   * 是否在接收到刷新事件時立即執行（不等待 onActivated）
   * 當設為 true 時，接收到 triggerListRefresh 會立即刷新
   * 當設為 false 時，會等到 onActivated 時才執行（適用於 keep-alive 場景）
   * @default false
   */
  refreshOnEvent?: boolean
}

/**
 * 通用列表刷新 Composable
 * 適用於所有使用 keep-alive 的列表頁面
 *
 * 支援兩種模式：
 * 1. 前端刪除模式：傳入 list 參數，從陣列中移除項目，保持滾動位置
 * 2. 完整刷新模式：不傳 list，調用 onRefresh 重新 fetch
 *
 * @example
 * ```ts
 * // 模式 1: 前端刪除（推薦，保持滾動位置）
 * const { list, resetAndFetch } = useInfiniteScrollList(...)
 * useListRefresh({ list, idField: 'custId', onRefresh: resetAndFetch })
 *
 * // 詳細頁面：傳入刪除的 ID
 * triggerListRefresh(customerId)
 * ```
 *
 * @example
 * ```ts
 * // 模式 2: 完整刷新（會丟失滾動位置）
 * const { resetAndFetch } = useInfiniteScrollList(...)
 * useListRefresh({ onRefresh: resetAndFetch })
 *
 * // 詳細頁面：不傳 ID
 * triggerListRefresh()
 * ```
 *
 * @example
 * ```ts
 * // 模式 3: 接收到事件時立即刷新（不使用 keep-alive 或不等待 onActivated）
 * const { resetAndFetch } = useInfiniteScrollList(...)
 * useListRefresh({
 *   onRefresh: resetAndFetch,
 *   immediate: false,      // 不在 onActivated 時執行
 *   refreshOnEvent: true   // 接收到事件時立即刷新
 * })
 *
 * // 詳細頁面：觸發刷新
 * triggerListRefresh() // 列表會立即刷新
 * ```
 */
export const useListRefresh = <TData>(options: UseListRefreshOptions<TData> = {}) => {
  const { listName, list, idField, onRefresh, immediate = true, refreshOnEvent = false } = options

  // 使用 Symbol 作為「未初始化」的標記，避免與有效值（number | undefined）混淆
  const PENDING = Symbol('pending')
  const pendingRemovedId = ref<number | string | undefined | typeof PENDING>(PENDING)

  // 執行刷新或刪除
  const refresh = async () => {
    const removedId = pendingRemovedId.value

    // 未初始化，跳過
    if (removedId === PENDING) {
      return
    }

    // 重置狀態
    pendingRemovedId.value = PENDING

    // 前端刪除模式（傳入了 ID）
    if (removedId !== undefined && list && idField) {
      const index = list.value.findIndex((item) => item[idField] === removedId)
      if (index !== -1) {
        list.value.splice(index, 1)
      }
      return
    }

    // 完整刷新模式（未傳入 ID）
    if (removedId === undefined && onRefresh) {
      await onRefresh()
    }
  }

  const handleRefreshEvent = (payload: TriggerListRefreshOptions | undefined) => {
    const { targetList, removedId } = payload ?? {}

    // 如果指定了目標列表，且當前列表名稱不匹配，則跳過
    if (targetList && listName && targetList !== listName) {
      return
    }

    pendingRemovedId.value = removedId

    // 如果設定為立即刷新，則在接收到事件時就執行
    if (refreshOnEvent) {
      refresh()
    }
  }

  onMounted(() => {
    eventEmitter.on(CommonEvent.REFRESH_LIST, handleRefreshEvent)
  })

  if (immediate) {
    onActivated(() => {
      refresh()
    })
  }

  onUnmounted(() => {
    eventEmitter.off(CommonEvent.REFRESH_LIST, handleRefreshEvent)
  })

  return {
    refresh
  }
}

/**
 * 觸發 keep alive 列表刷新
 * 在詳細頁面或其他需要通知列表刷新的地方調用
 *
 * @param options - 刷新選項，可指定目標列表和刪除項目 ID
 *
 * @example
 * ```ts
 * import { triggerListRefresh } from '@/composables/useListRefresh'
 *
 * // 指定刷新特定列表（名稱需與 router meta componentName 一致）
 * triggerListRefresh({ targetList: 'ShorterList' })
 *
 * // 前端刪除模式（保持滾動位置）
 * triggerListRefresh({ removedId: customerId })
 *
 * // 完整刷新所有列表
 * triggerListRefresh()
 * ```
 */
export const triggerListRefresh = (options?: TriggerListRefreshOptions) => {
  eventEmitter.emit(CommonEvent.REFRESH_LIST, options)
}
